import { Panel } from "../panel/Panel";
import { ComponentTypes } from "../../base/ComponentTypes";
import { WidgetPropDefinitions, WidgetProps } from "./WidgetProps";
import { Layout } from "../layout/Layout";
import { DataSource } from "../../databinding/DataSource";
import { DOMUtil, HorizontalAlignment, StringUtil, VerticalAlignment } from "@mcleod/core";
import { Button } from "../button/Button";
import { Label } from "../label/Label";
import { HorizontalSpacer } from "../../page/HorizontalSpacer";
import { Component } from "../../base/Component";
import { serializeComponents } from "../../serializer/ComponentSerializer";
import { ComponentPropDefinition } from "../../base/ComponentProps";
import { Image } from "../image/Image";

const _defaultProps = {
  borderColor: "strokeSecondary",
  borderRadius: 10,
  borderShadow: false,
  borderWidth: 1,
  padding: 0
};

/**
 * Widgets are self-contained components that can be placed onto containers
 * and display information that is useful to the user without being concerned with
 * what page they are on.  They mainly consist of an existing layout, which is
 * wrapped with a title bar, border, etc.
 *
 */
export class Widget extends Panel implements WidgetProps {
  public description: string;
  private _title: string;
  private _titleBar: Panel;
  private _titleLabel: Label;
  private _titleBarSpacer: HorizontalSpacer = new HorizontalSpacer();
  private _displayRefresh: boolean = false;
  private _refreshButton: Button;
  private _layout: Layout;
  private _busyWhenDataSourceBusy: boolean;
  private _busy: boolean;
  private _busyPanel: Panel;

  constructor(props?: Partial<WidgetProps>) {
    super({ ..._defaultProps, ...props });
    this.isRow = true;
  }

  get layoutName(): string {
    return this._layout?.layoutName;
  }

  set layoutName(value: string) {
    if (StringUtil.isEmptyString(value) === true) {
      this._layout?.mainDataSource?.removeBusyComponent(this);
      this.remove(this._layout);
      this._layout = null;
      return;
    }
    if (this._layout == null) {
      this._layout = Layout.getLayout(value, { fillRow: true, fillHeight: true, marginTop: 6, marginBottom: 6 });
      this._layout.addLayoutLoadListener(event => {
        this.syncTitleBar();
        this.minHeight = DOMUtil.getElementHeight(this._element);
        this.minWidth = DOMUtil.getElementWidth(this._element);
        this._layout.mainDataSource?.addBusyComponent(this);
        this._layout.mainDataSource?.search();
      });
      this.add(this._layout);
    }
  }

  get title(): string {
    return this._title;
  }

  set title(value: string) {
    this._title = value;
    this.syncTitleBar();
  }

  private getEffectiveTitle(): string {
    if (StringUtil.isEmptyString(this.title) === true && StringUtil.isEmptyString(this._layout?.title) !== true)
    return this._layout.title;
  return this.title;
  }

  get displayRefresh(): boolean {
    return this._displayRefresh || this.getPropertyDefinitions().displayRefresh.defaultValue;
  }

  set displayRefresh(value: boolean) {
    this._displayRefresh = value;
    this.syncTitleBar();
  }

  /**
   * Defines logic that construct's the widget's title bar.  This can be overridden if necessary.
   */
  public syncTitleBar() {
    if (StringUtil.isEmptyString(this.getEffectiveTitle()) === true && this.displayRefresh === false)
      this.removeTitleBar();
    else
      this.setupTitleBar();
  }

  private removeTitleBar() {
    if (this._titleBar == null)
      return;
    this._titleBar.remove(this._titleLabel);
    this._titleBar.remove(this._refreshButton);
    this.remove(this._titleBar);
    this._titleBar = null;
  }

  private setupTitleBar() {
    let evaluateSpacer = false;
    if (this._titleBar == null) {
      this._titleBar = new Panel({ fillRow: true, backgroundColor: "primary.darker" });
      this._titleBar._interactionEnabled = this.__designer == null;
      this.insert(this._titleBar, 0);
      evaluateSpacer = true;
    }
    if (StringUtil.isEmptyString(this.getEffectiveTitle()) !== true) {
      if (this._titleLabel == null) {
        this._titleLabel = new Label({ fontSize: "xlarge", color: "primary.reverse", rowBreak: false });
        this._titleLabel._interactionEnabled = this.__designer == null;
        this._titleBar.insert(this._titleLabel, 0);
        evaluateSpacer = true;
      }
      this._titleLabel.caption = this.getEffectiveTitle();
    }
    else {
      this._titleBar.remove(this._titleLabel);
      this._titleLabel = null;
      evaluateSpacer = true;
    }
    if (this._displayRefresh === true) {
      if (this._refreshButton == null) {
        this._refreshButton = new Button({ imageName: "refresh", onClick: event => this.refresh(), borderWidth: 0, color: "primary.reverse", padding: 4 });
        this._refreshButton._interactionEnabled = this.__designer == null;
        this._titleBar.add(this._refreshButton);
        evaluateSpacer = true;
      }
    }
    else {
      this._titleBar.remove(this._refreshButton);
      this._refreshButton = null;
      evaluateSpacer = true;
    }
    if (evaluateSpacer === true) {
      if (this._titleLabel != null && this._refreshButton != null) {
        for (const component of this._titleBar.components) {
          if (component instanceof HorizontalSpacer)
            return;
        }
        this._titleBar.insert(this._titleBarSpacer, 1);
      }
      else
        this._titleBar.remove(this._titleBarSpacer);
    }
  }

  get busyWhenDataSourceBusy(): boolean {
    return this._busyWhenDataSourceBusy || this.getPropertyDefinitions().busyWhenDataSourceBusy.defaultValue;
  }

  set busyWhenDataSourceBusy(value: boolean) {
    this._busyWhenDataSourceBusy = value;
  }

  get busy(): boolean {
    return this._busy == null ? false : this._busy;
  }

  set busy(value: boolean) {
    this._busy = value;
    if (value === true) {
      this._busyPanel = new Panel({ fillHeight: true, fillRow: true, margin: 0, padding: 0, align: HorizontalAlignment.CENTER, verticalAlign: VerticalAlignment.CENTER });
      this._busyPanel.add(new Image({ name: "spinner", marginTop: 10, rotate: true, color: "primary" }));
      this.remove(this._layout);
      this.add(this._busyPanel);
    }
    else {
      this.remove(this._busyPanel);
      this._busyPanel = null;
      if (this.contains(this._layout) !== true)
        this.add(this._layout);
    }
  }

  public refresh() {
    if (this._layout?.dataSourceIDs != null) {
      for (const dataSourceId of this._layout.dataSourceIDs) {
        (this._layout[dataSourceId] as DataSource).search();
      }
    }
  }

  override getPropertyDefinitions() {
    return WidgetPropDefinitions.getDefinitions();
  }

  override get serializationName(): string {
    return "widget";
  }

  _serializeNonProps():string {
    const componentsToSerialize: Component[] = []
    if (this.components != null && this.components.length > 0) {
      for (const component of this.components) {
        if (component != this._titleBar && component != this._layout)
          componentsToSerialize.push(component);
      }
    }
    if (componentsToSerialize.length > 0)
      return "\"components\": " + serializeComponents(componentsToSerialize, null,) + ",\n";
    return "";
  }

  override getPropertyDefaultValue(prop: ComponentPropDefinition): any {
    if (_defaultProps[prop.name] != null)
      return _defaultProps[prop.name];
    return super.getPropertyDefaultValue(prop);
  }
}

ComponentTypes.registerComponentType("widget", Widget.prototype.constructor);
